import { FirebaseApp, getApp, initializeApp } from 'firebase/app';
import {
  Auth,
  GoogleAuthProvider,
  UserCredential,
  browserPopupRedirectResolver,
  getAdditionalUserInfo,
  inMemoryPersistence,
  initializeAuth,
  signInWithCredential,
  signInWithPopup,
  signOut,
} from 'firebase/auth';
import { app } from '../firebase';

const CHECK_APP_NAME = 'google-account-check';

const googleProvider = new GoogleAuthProvider();
googleProvider.setCustomParameters({ prompt: 'select_account' });

let checkAuth: Auth | undefined;

// The account is picked on a separate, in-memory Auth instance so the app never sees a Google user
// until we know they are allowed in. Signing in on the main instance flips every auth route to
// /collabs at once, before the caller could check the email.
const getCheckAuth = (): Auth => {
  if (!checkAuth) {
    let checkApp: FirebaseApp;
    try {
      checkApp = getApp(CHECK_APP_NAME);
    } catch {
      checkApp = initializeApp(app.options, CHECK_APP_NAME);
    }
    checkAuth = initializeAuth(checkApp, {
      persistence: inMemoryPersistence,
      popupRedirectResolver: browserPopupRedirectResolver,
    });
  }
  return checkAuth;
};

/**
 * Runs Google sign-in and lets the main app sign in only when `isAllowed(email)` passes.
 * A Google account that is not allowed leaves no trace: the Auth user is deleted if this sign-in
 * created it (a stray user holding an address blocks that creator's email-change claim).
 * Popup, not redirect: a redirect needs the auth domain (storyitapp.firebaseapp.com) to share the
 * app's site, or Safari's storage partitioning drops the result.
 */
export const signInWithGoogle = async (
  mainAuth: Auth,
  isAllowed: (email: string) => Promise<boolean>
): Promise<{ signedIn: boolean; email: string | null }> => {
  const auth = getCheckAuth();
  const result: UserCredential = await signInWithPopup(auth, googleProvider);
  const email = result.user.email;
  let signedIn = false;
  try {
    if (email && (await isAllowed(email))) {
      const credential = GoogleAuthProvider.credentialFromResult(result);
      if (!credential) throw new Error('Google sign-in returned no credential');
      await signInWithCredential(mainAuth, credential);
      signedIn = true;
    }
    return { signedIn, email };
  } finally {
    // Also on errors: a failed check must not leave a new Google user holding the address.
    if (!signedIn && getAdditionalUserInfo(result)?.isNewUser) {
      await result.user.delete().catch((e) => console.error('Removing unused Google user failed:', e));
    }
    await signOut(auth);
  }
};

export const isGoogleSignInCancelled = (error: unknown): boolean => {
  const code = (error as { code?: unknown })?.code;
  return code === 'auth/popup-closed-by-user' || code === 'auth/cancelled-popup-request';
};

export const mapGoogleAuthError = (error: unknown): string => {
  switch ((error as { code?: unknown })?.code) {
    case 'auth/popup-closed-by-user':
    case 'auth/cancelled-popup-request':
      return 'Sign-in was cancelled.';
    case 'auth/account-exists-with-different-credential':
      return 'This email already signs in another way. Use your invite link or phone instead.';
    case 'auth/popup-blocked':
      return 'Pop-up was blocked. Please allow pop-ups and try again.';
    default:
      return 'Google sign-in failed. Please try again.';
  }
};

// Google refuses OAuth inside in-app browsers (disallowed_useragent), and creators often open
// links from Instagram, so the button is hidden there rather than failing after a tap.
export const isEmbeddedBrowser = (userAgent: string = navigator.userAgent): boolean =>
  /Instagram|FBAN|FBAV|FB_IAB|TikTok|musical_ly|BytedanceWebview/i.test(userAgent);
